import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useRefreshFlightData } from '@/lib/providers/use-providers';
import {
  emptySection,
  flightFacts,
  FLIGHT_INFO_SECTION,
  matchesFilter,
  type FlightRowState,
  type ProviderFilter,
  type ServiceSectionModel,
  type VendorAccount,
} from '@/lib/providers/providers-overview';
import type { FlightDataStatus, ProviderStatus } from '@/lib/providers/types';
import { DetailItem, DetailLayout, InUseNote, ProviderRow, StatusCell, type StatusTone } from './provider-table';
import { FixedMode } from './integration-mode';
import { ServiceSection } from './service-section';
import { relativeTime } from './format';

/**
 * "Flight information" — where each flight is and in what state. The first
 * service card. Its `lead` row is FlightAware (AeroAPI) pulled by the flight
 * predictor, whose detail holds the last pull, the page budget and the
 * operator-forced refresh (demo prep: the predictor re-pulls a carrier at most
 * every ~20h, so statuses go stale between pulls; "Refresh now" forces a pull
 * NOW, budget-capped upstream). M2: the `flight-info` bindings an app declares
 * — the airline's service adapter, which wins every field it reports (D3) —
 * are ordinary rows of the same card; when the order of use locks the airline
 * provider first («La aerolínea siempre gana», 2026-10-29) its row heads the
 * card, above FlightAware.
 *
 * An unreadable or unreachable predictor reads "No answer" — never "not
 * configured", and it never counts under the "off" filter.
 */

/** `network` key per refused forced pull (`outcomes[].reason`) — the wire values
 *  stay verbatim; only the operator copy they map to is translated. */
const REFRESH_SKIP_KEY: Record<string, string> = {
  fresh: 'providers.flightData.skip.fresh',
  'in-progress': 'providers.flightData.skip.inProgress',
  'claim-lost': 'providers.flightData.skip.claimLost',
  'attempts-exhausted': 'providers.flightData.skip.attemptsExhausted',
  'budget-exhausted': 'providers.flightData.skip.budgetExhausted',
  'mode-off': 'providers.flightData.skip.modeOff',
  'key-not-configured': 'providers.flightData.skip.keyNotConfigured',
  'not-registered': 'providers.flightData.skip.notRegistered',
  error: 'providers.flightData.skip.error',
};

const STATE_TONE: Record<FlightRowState, StatusTone> = {
  loading: 'checking',
  unreadable: 'unknown',
  not_configured: 'warn',
  unreachable: 'unknown',
  pull_failed: 'fail',
  never_pulled: 'neutral',
  online: 'ok',
};

const STATE_LABEL: Record<FlightRowState, string> = {
  loading: 'providers.rowStatus.label.checking',
  unreadable: 'providers.rowStatus.label.unknown',
  not_configured: 'providers.rowStatus.label.not_configured',
  unreachable: 'providers.rowStatus.label.unknown',
  pull_failed: 'providers.rowStatus.label.pull_failed',
  never_pulled: 'providers.rowStatus.label.never_pulled',
  online: 'providers.rowStatus.label.online',
};

/** "3m ago · 153 flights · failed" — every segment translated, in a fixed order. */
function lastPullText(t: TFunction<'network'>, data: FlightDataStatus | undefined, loading: boolean): string {
  const lastPull = data?.lastPull;
  if (!lastPull) return loading ? '…' : t('providers.flightData.never');
  const parts = [relativeTime(lastPull.finishedAt ?? lastPull.startedAt, t)];
  if (lastPull.flightsUpserted != null) {
    parts.push(t('providers.flightData.pullFlights', { n: lastPull.flightsUpserted }));
  }
  if (lastPull.status === 'error') parts.push(t('providers.flightData.pullFailed'));
  return parts.join(' · ');
}

function stateHint(t: TFunction<'network'>, state: FlightRowState, pull: string): string {
  if (state === 'online' || state === 'pull_failed') return t('providers.rowStatus.hint.lastPull', { when: pull });
  if (state === 'loading') return t('providers.rowStatus.hint.checking');
  if (state === 'never_pulled') return t('providers.rowStatus.hint.never_pulled');
  if (state === 'not_configured') return t('providers.rowStatus.hint.not_configured');
  if (state === 'unreachable') return t('providers.rowStatus.hint.unreachable');
  return t('providers.rowStatus.hint.unreadable');
}

interface FlightRowProps {
  state: FlightRowState;
  data: FlightDataStatus | undefined;
  isLoading: boolean;
}

interface FlightInfoProps extends FlightRowProps {
  /** The declared `flight-info` bindings, when the inventory answered. */
  section?: ServiceSectionModel | undefined;
  filter?: ProviderFilter;
  traffic?: ProviderStatus[] | undefined;
  accounts?: ReadonlyMap<string, VendorAccount> | undefined;
}

export function FlightInfoSection({ section, filter = 'all', traffic, accounts, ...row }: FlightInfoProps) {
  const facts = flightFacts(row.state);
  return (
    <ServiceSection
      section={section ?? emptySection(FLIGHT_INFO_SECTION)}
      filter={filter}
      traffic={traffic}
      accounts={accounts}
      lead={{ facts, row: <FlightInfoRow {...row} hidden={!matchesFilter(facts, filter)} /> }}
    />
  );
}

function FlightInfoRow({ state, data, isLoading, hidden }: FlightRowProps & { hidden: boolean }) {
  const { t } = useTranslation('network');
  const refresh = useRefreshFlightData();
  const facts = flightFacts(state);
  const pull = lastPullText(t, data, isLoading);

  const result = refresh.data;
  const pulledFlights = result?.outcomes.reduce((acc, o) => acc + (o.flightsUpserted ?? 0), 0) ?? 0;
  const skipReason = result && !result.pulled ? result.outcomes.find((o) => o.reason)?.reason : undefined;
  const skipKey = REFRESH_SKIP_KEY[skipReason ?? ''];

  return (
    <ProviderRow
      testId="flight-data-card"
      dataState={state}
      dataMode={facts.mode ?? undefined}
      hidden={hidden}
      dimmed={state === 'not_configured'}
      name={t('providers.integrations.vendorName.aeroapi')}
      sub={
        data?.icao
          ? t('providers.flightInfo.viaCarrier', { icao: data.icao })
          : t('providers.flightInfo.via')
      }
      expandTestId="flight-data-expand"
      mode={
        <FixedMode
          mode={facts.mode}
          category={FLIGHT_INFO_SECTION}
          testId="flight-data-mode"
          fixedTestId="flight-data-mode-fixed"
        />
      }
      status={
        <StatusCell
          tone={STATE_TONE[state]}
          label={t(STATE_LABEL[state])}
          hint={stateHint(t, state, pull)}
          state={state}
          labelTestId="flight-data-status"
          hintTestId="flight-data-status-hint"
        />
      }
      usedFor={t('providers.flightInfo.usedFor')}
      inUse={<InUseNote>{t('providers.inUseCell.fixed')}</InUseNote>}
      detailTestId="flight-data-detail"
      renderDetail={() => (
        <DetailLayout
          facts={
            <>
              <DetailItem label={t('providers.flightInfo.source')}>{t('providers.flightInfo.sourceValue')}</DetailItem>
              <DetailItem label={t('providers.flightData.carrier')} className="font-mono">
                {data?.icao || '—'}
              </DetailItem>
              <DetailItem label={t('providers.flightData.lastPull')} testId="flight-data-last-pull">
                {pull}
              </DetailItem>
              {data?.pagesLeft != null ? (
                <DetailItem label={t('providers.flightData.pageBudget')} className="tabular-nums">
                  {data.pagesLeft}
                  {data.dailyPageBudget ? ` / ${data.dailyPageBudget}` : ''}
                </DetailItem>
              ) : null}
              {data?.mode ? (
                <DetailItem label={t('providers.flightInfo.predictorMode')} className="font-mono">
                  {data.mode}
                </DetailItem>
              ) : null}
              {data?.minIntervalHours != null ? (
                <DetailItem label={t('providers.flightInfo.minInterval')}>
                  {t('providers.flightInfo.minIntervalValue', { n: data.minIntervalHours })}
                </DetailItem>
              ) : null}
            </>
          }
          actions={
            <div className="space-y-2">
              <Button
                variant="outline"
                className="min-h-11 w-full"
                onClick={() => refresh.mutate()}
                disabled={refresh.isPending || isLoading || !data?.configured}
                data-testid="flight-data-refresh"
              >
                <RefreshCw className={`h-4 w-4 ${refresh.isPending ? 'animate-spin' : ''}`} aria-hidden />
                {refresh.isPending ? t('providers.flightData.refreshing') : t('providers.flightData.refresh')}
              </Button>
              {refresh.isError ? (
                <p className="text-xs text-rose-700 dark:text-rose-400" data-testid="flight-data-refresh-error">
                  {t('providers.flightData.refreshError')}
                </p>
              ) : result ? (
                <p className="text-xs" data-testid="flight-data-refresh-result">
                  {result.pulled ? (
                    <span className="text-emerald-700 dark:text-emerald-400">
                      {t('providers.flightData.pulled', { flights: pulledFlights, pages: result.pagesUsed })}
                    </span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-400">
                      {skipKey ? t(skipKey) : t('providers.flightData.skip.default')}
                    </span>
                  )}
                </p>
              ) : data && !data.configured ? (
                <p className="text-xs text-muted-foreground">{t('providers.flightData.notConfigured')}</p>
              ) : data && !data.reachable ? (
                <p className="text-xs text-amber-700 dark:text-amber-400">{t('providers.flightData.unreachable')}</p>
              ) : null}
            </div>
          }
        />
      )}
    />
  );
}
